import { z } from "zod";

/**
 * Chart datasets for the Explore pages (docs/plans/2026-09-14-explore-pages.md, ADR-008).
 * Mirror of `pipeline/plenum/charts.py` (ChartDataset, ChartManifest). The pipeline's
 * publish_charts step writes one JSON per slug to R2 and KV; the Worker serves it read-only at
 * GET /api/charts/:slug and the manifest at GET /api/charts/manifest. Every dataset carries its
 * sources, vintage, method and ingest run, so a chart page shows the same provenance a claim does.
 */

export const ChartScalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type ChartScalar = z.infer<typeof ChartScalar>;

export const ChartView = z.enum(["map", "bar", "histogram", "scatter", "line", "table"]);
export type ChartView = z.infer<typeof ChartView>;

export const ChartTopic = z.enum([
  "method",
  "fleet",
  "operations",
  "planned",
  "climate",
  "prospects",
  "coverage",
]);
export type ChartTopic = z.infer<typeof ChartTopic>;

export const ChartSource = z.object({ name: z.string(), url: z.string().url().nullable() });
export type ChartSource = z.infer<typeof ChartSource>;

export const CHART_SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const ChartMeta = z.object({
  slug: z.string().regex(CHART_SLUG),
  title: z.string(),
  description: z.string(),
  method: z.string(),
  unit: z.string(),
  topic: ChartTopic,
  views: z.array(ChartView).min(1),
  dims: z.array(z.string()),
  measures: z.array(z.string()).min(1),
  keywords: z.array(z.string()),
  sources: z.array(ChartSource).min(1),
  vintage: z.record(z.string()),
  params: z.record(z.unknown()),
  generated_at: z.string(),
  ingest_run_id: z.number().int().nullable(),
  model_version: z.string().nullable(),
  row_count: z.number().int().nonnegative(),
});
export type ChartMeta = z.infer<typeof ChartMeta>;

export const ChartRow = z.record(ChartScalar);
export type ChartRow = z.infer<typeof ChartRow>;

export const ChartDataset = z
  .object({ meta: ChartMeta, rows: z.array(ChartRow) })
  .refine((d) => d.meta.row_count === d.rows.length, {
    message: "row_count does not match rows",
  });
export type ChartDataset = z.infer<typeof ChartDataset>;

export const ChartCatalogEntry = z.object({
  slug: z.string().regex(CHART_SLUG),
  title: z.string(),
  description: z.string(),
  topic: ChartTopic,
  views: z.array(ChartView).min(1),
  unit: z.string(),
  dims: z.array(z.string()),
  measures: z.array(z.string()),
  keywords: z.array(z.string()),
  row_count: z.number().int().nonnegative(),
  size_bytes: z.number().int().nonnegative(),
  size_bytes_gzip: z.number().int().nonnegative(),
  sha256: z.string().length(64),
});
export type ChartCatalogEntry = z.infer<typeof ChartCatalogEntry>;

export const ChartManifest = z.object({
  version: z.string(),
  generated_at: z.string(),
  ingest_run_id: z.number().int().nullable(),
  charts: z.array(ChartCatalogEntry),
});
export type ChartManifest = z.infer<typeof ChartManifest>;

/** What GET /api/charts/manifest returns before the first publish. */
export type ChartManifestResponse =
  | ChartManifest
  | { version: null; generated_at: null; ingest_run_id: null; charts: [] };
